const clampCopies = (value, fallback = 1) =>
  Math.max(1, Math.min(99, parseInt(value, 10) || fallback));

const COPY_BADGE_CSS = `
.native-print-copy-badge {
  position: absolute;
  z-index: 20;
  box-sizing: border-box;
  min-width: 10mm;
  padding: 1mm 1.5mm;
  border: 0.3mm solid #111;
  background: #fff;
  color: #000;
  font: 700 11pt/1 Arial, Helvetica, sans-serif;
  text-align: center;
}
.packing-slip-page { position: relative; }
.packing-slip-page > .native-print-copy-badge {
  right: 5mm;
  bottom: 5mm;
}
.go4-outside-page { position: relative; }
.go4-outside-page > .native-print-copy-badge {
  right: 3mm;
  bottom: 3mm;
}
.lz-label-host > .native-print-copy-badge {
  left: 50%;
  bottom: 4mm;
  transform: translateX(-50%);
}
`;

/**
 * Expand explicit physical pages into complete per-order copy groups for one
 * native browser print job. Copy 1 includes all continuations before copy 2.
 */
export function prepareHtmlForNativeBatchPrint(
  fullHtml,
  { copiesPerOrder = 1, copiesByOrder = {} } = {},
) {
  if (typeof DOMParser === 'undefined') return fullHtml;
  const parser = new DOMParser();
  const doc = parser.parseFromString(String(fullHtml || ''), 'text/html');
  let blocks = Array.from(doc.querySelectorAll('.lz-sheet'));
  let mode = 'lz-sheet';
  if (!blocks.length) {
    blocks = Array.from(doc.querySelectorAll('.packing-slip-page'));
    mode = 'packing-slip-page';
  }
  if (!blocks.length) {
    blocks = Array.from(doc.querySelectorAll('.go4-outside-page'));
    mode = 'go4-outside-page';
  }
  if (!blocks.length) return fullHtml;

  const groups = [];
  blocks.forEach((block, index) => {
    const orderKey = block.getAttribute('data-print-order') || `page-${index}`;
    const previous = groups[groups.length - 1];
    if (previous?.orderKey === orderKey) previous.blocks.push(block);
    else groups.push({ orderKey, blocks: [block] });
  });

  const parent = blocks[0].parentElement;
  const fragment = doc.createDocumentFragment();
  let physicalPageIndex = 0;
  groups.forEach((group) => {
    const copies = clampCopies(
      copiesByOrder?.[group.orderKey],
      clampCopies(copiesPerOrder),
    );
    for (let copy = 1; copy <= copies; copy += 1) {
      group.blocks.forEach((block) => {
        const clone = block.cloneNode(true);
        clone.setAttribute('data-copy-index', String(copy));
        clone.setAttribute('data-copy-count', String(copies));
        if (mode === 'lz-sheet' && physicalPageIndex > 0) {
          clone.classList.add('lz-sheet--break');
        }
        if (copies > 1) {
          const badge = doc.createElement('div');
          badge.className = 'native-print-copy-badge';
          badge.textContent = `${copy}/${copies}`;
          const badgeHost =
            mode === 'lz-sheet' ? clone.querySelector('.lz-label-host') : clone;
          badgeHost?.appendChild(badge);
        }
        fragment.appendChild(clone);
        physicalPageIndex += 1;
      });
    }
  });

  blocks.forEach((block) => block.remove());
  parent.appendChild(fragment);
  const style = doc.createElement('style');
  style.setAttribute('data-native-copy-badges', 'true');
  style.textContent = COPY_BADGE_CSS;
  doc.head.appendChild(style);
  return `<!DOCTYPE html>\n${doc.documentElement.outerHTML}`;
}

export default prepareHtmlForNativeBatchPrint;
